import { TextButton } from '@vgrubs/components';
import { useLayoutEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { lightTheme } from '../../../styles/theme';
import type { ActiveAnnouncement } from '../../../types/api/announcements.types';
import {
  Card,
  CardBody,
  CardDate,
  CardMarkdown,
  CardMedia,
  CardMediaFrame,
  CardTitle,
  ReadMoreRow,
} from '../WhatsNew.styled';

/** When it came out: the release date, or when it was written if it had none. */
const releasedAt = (item: ActiveAnnouncement) =>
  item.publishedAt ?? item.createdAt;

/** "Aug 14, 2026", as the merchant app writes it. */
const formatReleased = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/**
 * One release on the What's New page. The body shows five lines; a longer
 * one gets Read more, which opens the whole announcement in a modal - the
 * same view the popup draws - rather than stretching its row of the grid.
 *
 * Whether it is longer is measured, not guessed from the character count:
 * markdown lists and headings take lines the text length does not show.
 */
export const WhatsNewCard = ({
  item,
  onReadMore,
}: {
  item: ActiveAnnouncement;
  onReadMore: (item: ActiveAnnouncement) => void;
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();

    // The card's width - and with it the line count - changes with the grid.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [item.body]);

  const media = item.gifUrl ?? item.imageUrl;
  const date = releasedAt(item);

  return (
    <Card>
      {media && (
        <CardMediaFrame>
          <CardMedia src={media} alt="" />
        </CardMediaFrame>
      )}
      <CardBody>
        <CardDate dateTime={date}>{formatReleased(date)}</CardDate>
        <CardTitle>{item.title}</CardTitle>
        <CardMarkdown ref={bodyRef} $faded={overflows}>
          <ReactMarkdown>{item.body}</ReactMarkdown>
        </CardMarkdown>

        {overflows && (
          <ReadMoreRow>
            <TextButton
              color={lightTheme.primary}
              onClick={() => onReadMore(item)}
            >
              Read more
            </TextButton>
          </ReadMoreRow>
        )}
      </CardBody>
    </Card>
  );
};
